/**
 * Cola de mutaciones offline en IndexedDB.
 *
 * Cuando la app está sin conexión y el usuario hace una mutación (ej.
 * registrar avance de una sub-partida), en lugar de fallar la guardamos
 * acá con `enqueue()`. Al recuperar conexión, `drenar()` las despacha
 * en orden y las elimina al confirmar.
 *
 * Por ahora solo está la infraestructura — los handlers concretos por
 * tipo se enchufan en Fase 3 (captura de avances).
 */

import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

const DB_NAME = 'obrascontrol';
const DB_VERSION = 1;
const STORE = 'mutaciones';

/** Tipos de mutación que se pueden encolar. Extender en Fase 3. */
export type TipoMutacion = 'avance.registrar' | 'avance.actualizar' | 'cierre.semana' | 'reservado';

export interface MutacionPendiente {
  id?: number;
  tipo: TipoMutacion;
  payload: unknown;
  creado_en: string; // ISO
  intentos: number;
  ultimo_error?: string | null;
}

interface SchemaDB extends DBSchema {
  mutaciones: {
    key: number;
    value: MutacionPendiente;
    indexes: { 'by-tipo': TipoMutacion; 'by-creado': string };
  };
}

let _dbPromise: Promise<IDBPDatabase<SchemaDB>> | null = null;

function abrirDB(): Promise<IDBPDatabase<SchemaDB>> {
  if (!_dbPromise) {
    _dbPromise = openDB<SchemaDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('by-tipo', 'tipo');
          store.createIndex('by-creado', 'creado_en');
        }
      },
    });
  }
  return _dbPromise;
}

/** Agrega una mutación a la cola. Devuelve el id asignado por IndexedDB. */
export async function enqueue(tipo: TipoMutacion, payload: unknown): Promise<number> {
  const db = await abrirDB();
  const id = await db.add(STORE, {
    tipo,
    payload,
    creado_en: new Date().toISOString(),
    intentos: 0,
    ultimo_error: null,
  });
  return id as number;
}

/** Cuántas mutaciones pendientes hay. Útil para el indicador del header. */
export async function contar(): Promise<number> {
  const db = await abrirDB();
  return db.count(STORE);
}

/** Lista todas las mutaciones pendientes ordenadas por fecha de creación. */
export async function listar(): Promise<MutacionPendiente[]> {
  const db = await abrirDB();
  return db.getAllFromIndex(STORE, 'by-creado');
}

/** Elimina una mutación de la cola (después de despacharla con éxito). */
export async function eliminar(id: number): Promise<void> {
  const db = await abrirDB();
  await db.delete(STORE, id);
}

/** Marca un intento fallido — incrementa contador y guarda el último error. */
export async function marcarError(id: number, error: string): Promise<void> {
  const db = await abrirDB();
  const existente = await db.get(STORE, id);
  if (!existente) return;
  await db.put(STORE, {
    ...existente,
    intentos: existente.intentos + 1,
    ultimo_error: error,
  });
}

/** Función que sabe despachar una mutación de un tipo dado contra el backend. */
export type Despachador = (m: MutacionPendiente) => Promise<void>;

export interface ResultadoDrenado {
  ok: number;
  fallidas: number;
}

/**
 * Drena la cola: despacha en orden cada mutación pendiente usando el
 * `despachador` del mapa. Las que confirman se eliminan; las que fallan se
 * marcan con el error y se dejan en la cola para reintentar luego.
 *
 * Se detiene si una mutación falla por error de red (asume que seguimos sin
 * conexión) para no quemar reintentos de toda la cola.
 */
export async function drenar(
  dispatchers: Partial<Record<TipoMutacion, Despachador>>,
): Promise<ResultadoDrenado> {
  const pendientes = await listar();
  let ok = 0;
  let fallidas = 0;

  for (const m of pendientes) {
    const despachar = dispatchers[m.tipo];
    if (!despachar || m.id === undefined) continue;
    try {
      await despachar(m);
      await eliminar(m.id);
      ok++;
    } catch (e) {
      const mensaje = e instanceof Error ? e.message : 'Error desconocido';
      await marcarError(m.id, mensaje);
      fallidas++;
      // Error de red → probablemente seguimos offline: cortar el drenado.
      if (!navigator.onLine) break;
    }
  }

  return { ok, fallidas };
}
